import { trackError } from "@/components/error/trackError";
import { fireAndForget } from "@/lib/vercelUtils";
import { assertNever } from "@/packages/ts-utils";
import { postJson } from "@/utils/routeUtils";
import { trackSystemMetric } from "@/utils/systemMetrics/useSystemMetrics";
import posthog from "posthog-js";

let latestRequest = 0;
let latestAppliedRequest = 0;
// Bumped on logout, so a response for the previous user never applies.
let sessionEpoch = 0;
const requestsInFlight = new Set<number>();
// Null while the loaded flags came from storage or no bootstrap succeeded yet.
let bootstrappedDistinctId: string | null = null;

/** The distinct id the applied flags were evaluated for. */
export const getBootstrappedDistinctId = () => bootstrappedDistinctId;

// Call on logout, before the next bootstrap starts.
export const invalidateBootstrappedFlags = () => {
  sessionEpoch += 1;
  requestsInFlight.clear();
  bootstrappedDistinctId = null;
};

const recordLoad = (outcome: "loaded" | "error") =>
  trackSystemMetric({ name: "feature_flags.browser.load", tags: { outcome } });

// Keeps flags that already loaded and waits for bootstraps still in flight; otherwise settles on the flags this browser last stored (all-off when none) so loading gates resolve.
const settleAfterFailure = () => {
  recordLoad("error");
  const canStillLoad = requestsInFlight.size > 0;
  if (!canStillLoad && !posthog.featureFlags.hasLoadedFlags) {
    // posthog-js persists the last applied flags until posthog.reset() on logout.
    posthog.featureFlags.updateFlags(
      posthog.featureFlags.getFlagVariants(),
      posthog.featureFlags.getFlagPayloads()
    );
  }
};

// The newest applied response wins; an older success still applies when every newer request failed, as in posthog-js.
export const applyBootstrappedFlags = async (): Promise<void> => {
  if (typeof window === "undefined") return;
  const requestId = ++latestRequest;
  const requestEpoch = sessionEpoch;
  const distinctId = posthog.get_distinct_id();
  requestsInFlight.add(requestId);
  try {
    const result = await postJson(
      "/api/feature-flags/bootstrap",
      { distinct_id: distinctId },
      { skipLogs: true, retries: 1 }
    ).finally(() => {
      requestsInFlight.delete(requestId);
    });
    if (requestEpoch !== sessionEpoch) return;
    // Dropped connections and PostHog timeouts are expected; the metric and the server monitor count them.
    if (!result.success) {
      settleAfterFailure();
      return;
    }
    const { data } = result;
    switch (data.status) {
      case "unavailable":
        settleAfterFailure();
        return;
      case "ok":
        if (requestId < latestAppliedRequest) return;
        latestAppliedRequest = requestId;
        // Set before updateFlags, which notifies the flag listeners synchronously.
        bootstrappedDistinctId = distinctId;
        posthog.featureFlags.updateFlags(data.flags, data.payloads);
        recordLoad("loaded");
        return;
      default:
        assertNever(data);
    }
  } catch (error) {
    if (requestEpoch === sessionEpoch) settleAfterFailure();
    fireAndForget(
      trackError({
        error:
          error instanceof Error ? error : new Error("flag bootstrap failed"),
        componentStack: null,
        eventId: null,
        errorPlace: "applyBootstrappedFlags",
      })
    );
  }
};
